import { useCallback } from 'react';
import level62LogoWhite from '../../../../assets/icons/lvl-62-logo-simple-white.svg';
import level62LogoBlack from '../../../../assets/icons/lvl-62-logo-simple-black.svg';
import spaceforceIconWhite from '../../../../assets/icons/spaceforce-icon-white.svg';
import spaceforceIconBlack from '../../../../assets/icons/spaceforce-icon-black.svg';
import level62IconWhite from '../../../../assets/icons/lvl-62-icon-white.svg';
import level62IconBlack from '../../../../assets/icons/lvl-62-icon-black.svg';
import spacePatchIcon from '../../../../assets/icons/space-patch-icon.svg';
import spaceSpecialtyIcon from '../../../../assets/icons/space-specialty-icon.svg';
import spaceCvIcon from '../../../../assets/icons/space-core-value-icon.svg';
import earthPatchIcon from '../../../../assets/icons/earth-patch-icon.svg';
import earthSpecialtyIcon from '../../../../assets/icons/earth-specialty-icon.svg';
import earthCvIcon from '../../../../assets/icons/earth-core-value-icon.svg';
import { PATCH_IMPORTS, resolvePatchKey } from '../utils/patchAssets';

const IMAGE_MAP = {
  'lvl-62-logo-simple-white': level62LogoWhite,
  'lvl-62-logo-simple-black': level62LogoBlack,
  'spaceforce-icon-white': spaceforceIconWhite,
  'spaceforce-icon-black': spaceforceIconBlack,
  'lvl-62-icon-white': level62IconWhite,
  'lvl-62-icon-black': level62IconBlack,
  'patch-icon-white': spacePatchIcon,
  'specialty-icon-white': spaceSpecialtyIcon,
  'core-value-icon-white': spaceCvIcon,
  'patch-icon-black': earthPatchIcon,
  'specialty-icon-black': earthSpecialtyIcon,
  'core-value-icon-black': earthCvIcon
};

export const useAssetLoader = () => {
  const getLocalImageSrc = useCallback((acfImageData, theme) => {
    if (!acfImageData) return null;

    const acfUrl = acfImageData.url || acfImageData;
    if (!acfUrl) return null;

    let filename = acfUrl.split('/').pop()?.split('.')[0];
    filename = filename?.replace(/-\d+$/, '');

    if (IMAGE_MAP[filename]) {
      return IMAGE_MAP[filename];
    }

    const baseName = filename?.replace(/-white$/, '').replace(/-black$/, '');
    const themeVariant = theme === 'space-theme' ? 'white' : 'black';
    const themedFilename = `${baseName}-${themeVariant}`;

    if (IMAGE_MAP[themedFilename]) {
      return IMAGE_MAP[themedFilename];
    }

    return null;
  }, []);

  const loadThemedAssets = useCallback(async (backgroundClass) => {
    const isSpaceTheme = backgroundClass === 'space-theme';

    try {
      let backgroundImage = null;
      let spaceforceAccent = null;

      try {
        const bgImageModule = isSpaceTheme
          ? await import('../../../../assets/images/bp-space-bg-image.png')
          : await import('../../../../assets/images/bp-earth-bg-image.png');
        backgroundImage = bgImageModule.default;
      } catch (error) {
        // Background image not found
      }

      try {
        const logoModule = isSpaceTheme
          ? await import('../../../../assets/icons/spaceforce-logo-purple.png')
          : await import('../../../../assets/icons/spaceforce-logo-orange.png');
        spaceforceAccent = logoModule.default;
      } catch (error) {
        // Logo accent not found
      }

      return {
        backgroundImage,
        spaceforceAccent
      };
    } catch (error) {
      return {
        backgroundImage: null,
        spaceforceAccent: null
      };
    }
  }, []);

  const loadPassengerInfoIcons = useCallback((theme) => {
    const themeVariant = theme === 'space-theme' ? 'white' : 'black';

    return {
      patch: IMAGE_MAP[`patch-icon-${themeVariant}`] || null,
      specialty: IMAGE_MAP[`specialty-icon-${themeVariant}`] || null,
      coreValue: IMAGE_MAP[`core-value-icon-${themeVariant}`] || null
    };
  }, []);

  const loadPatchImage = useCallback(async (patchImageUrl, fileName) => {
    if (!patchImageUrl) {
      throw new Error('No patch image URL provided');
    }

    const patchKey = resolvePatchKey(fileName, patchImageUrl);

    if (!patchKey) {
      throw new Error(`Unknown patch: ${fileName || patchImageUrl}`);
    }

    try {
      const patchModule = await PATCH_IMPORTS[patchKey]();
      const patchSrc = patchModule.default;

      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => reject(`Failed to load patch: ${patchKey}`);
        img.src = patchSrc;
      });
    } catch (error) {
      throw new Error(`Patch not found: ${patchKey}`);
    }
  }, []);

  const loadImage = useCallback((src) => {
    return new Promise((resolve, reject) => {
      if (!src) {
        reject(new Error('Empty image source'));
        return;
      }

      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
      img.src = src;
    });
  }, []);

  return {
    getLocalImageSrc,
    loadThemedAssets,
    loadPassengerInfoIcons,
    loadPatchImage,
    loadImage
  };
};
